import { useLocale } from '../contexts/LocaleContext'

/**
 * Format an ISO date string for display in list/detail pages.
 *
 * Returns "—" for:
 *  - null / undefined / empty inputs
 *  - invalid dates
 *  - dates before year 2000 (e.g. the "zero time" `0001-01-01T00:00:00Z`
 *    that the Go backend emits for un-populated timestamps)
 */
export function formatCreated(iso: string | undefined | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (isNaN(d.getTime()) || d.getFullYear() < 2000) return '—'
  return d.toLocaleDateString()
}

export function useFormatters() {
  const { locale } = useLocale()

  return {
    formatDate: (date: Date | string, options?: Intl.DateTimeFormatOptions) =>
      new Intl.DateTimeFormat(locale, options).format(new Date(date)),

    /**
     * Locale-aware number. Capped at one decimal by default — the product-wide
     * rule (see `formatDisplayNumber`) — but a caller that genuinely needs more
     * can still say so explicitly through `options`.
     */
    formatNumber: (value: number, options?: Intl.NumberFormatOptions) =>
      new Intl.NumberFormat(locale, { maximumFractionDigits: 1, ...options }).format(value),

    /**
     * Format a monetary amount in MAJOR units with its currency.
     *
     * There is deliberately no default currency. The euro default this helper
     * used to carry stamped € on every amount in the product, including a
     * British rent and an American service charge — a false statement about
     * money, made silently. A missing currency now renders as a plain number,
     * which is merely incomplete rather than wrong, and visibly so.
     */
    formatCurrency: (value: number, currency?: string | null) =>
      currency
        ? new Intl.NumberFormat(locale, { style: 'currency', currency }).format(value)
        : new Intl.NumberFormat(locale).format(value),

    /**
     * Format a monetary amount stored in MINOR units (cents) with its currency.
     *
     * Every amount the API carries is in minor units, and every call site used
     * to divide by 100 itself — so the division and the currency fallback were
     * copy-pasted together, fallback included. One helper, one rule: money is
     * never rendered without saying what money it is.
     */
    formatMoney: (minorUnits: number | null | undefined, currency?: string | null) => {
      if (minorUnits === null || minorUnits === undefined) return '—'
      const value = minorUnits / 100
      return currency
        ? new Intl.NumberFormat(locale, { style: 'currency', currency }).format(value)
        : new Intl.NumberFormat(locale).format(value)
    },

    formatPercent: (value: number) =>
      new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 1 }).format(value),
  }
}

/**
 * Round a number to at most one decimal for display.
 *
 * Product rule: nothing shown in the product carries more than one decimal.
 * A surface derived from a polygon comes back as 42.333333333333336 and was
 * printed in full on the Plan tab — fourteen decimals of arithmetic noise on
 * a measurement whose real precision is the centimetre at best.
 *
 * Rounding only, deliberately: no thousands separator and no padded decimal,
 * so a year (1987) stays `1987` rather than becoming `1 987`, an integer count
 * stays an integer, and the decimal mark stays the one every existing screen
 * already showed. Callers that genuinely need more precision — geographic
 * coordinates, per-call AI cost in dollars, CSS percentages — must not route
 * through here; they are not "numbers the reader reads", they are payloads.
 */
export function formatDisplayNumber(value: number): string {
  if (!Number.isFinite(value)) return String(value)
  return String(Number(value.toFixed(1)))
}
